import { useState, useEffect, useRef } from 'react'
import { PlusIcon, PencilSquareIcon, TrashIcon, DocumentArrowDownIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { utils as XLSXUtils, write as XLSXWrite } from 'xlsx'
import JsPDF from 'jspdf'
import 'jspdf-autotable'
import 'styles/ActivitiesManagement.css'
import { useTranslation } from 'react-i18next'
import { getFormattedMissingFieldsMessage } from 'utils'
import { useAuth } from 'auth/AuthContext'
import { makeRequest } from 'services/request.service'
import { showAlert } from 'services/alerts.service'
import EditActivityModal from 'components/modals/EditActivityModal'
import AddActivityModal from 'components/modals/AddActivityModal'
import ButtonComponent from 'components/common/ButtonComponent'
import TableComponent from 'components/common/TableComponent'
import ActionCardComponent from 'components/common/ActionCardComponent'
import PageLoadingState from 'components/common/PageLoadingState'
import ModalComponent from 'components/common/ModalComponent'

const ActivitiesManagement = () => {
  const hasFetched = useRef(false)
  const { user } = useAuth()
  const { t } = useTranslation()
  const [state, setState] = useState({
    activities: [],
    loading: true,
    isEditActivityModalOpen: false,
    isAddActivityModalOpen: false,
    selectedActivity: null,
    columnName: '',
    orderBy: 'ASC',
    filter: '',
    page: 1,
    limit: 10,
    searchTerm: '',
    showDeleteConfirm: false,
    pendingDelete: null
  })

  const { activities } = state

  const getUrl = () => {
    const params = new URLSearchParams({
      filter: state.filter || ''
    })

    if (state.page) params.set('page', state.page)
    if (state.limit) params.set('limit', state.limit)
    if (state.columnName) params.set('columnName', state.columnName)
    if (state.orderBy) params.set('orderBy', state.orderBy)

    return `/management/activities/${user.farmId}?${params.toString()}`
  }

  const fetchActivities = async () => {
    try {
      const response = await makeRequest({
        method: 'get',
        url: getUrl(),
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
      })
      if (response.status === 200) {
        setState(s => ({ ...s, activities: response.data, loading: false }))
      }
    } catch (error) {
      setState((s) => ({
        ...s,
        error: error.message,
        selectedActivity: null,
        loading: false
      }))
    }
  }

  useEffect(() => {
    if (!user?.farmId) return
    if (hasFetched.current) return
    hasFetched.current = true

    fetchActivities()
  }, [user?.farmId, state.filter, state.page, state.limit, state.columnName, state.orderBy])

  const handleExportExcel = () => {
    try {
      const dataToExport = activities.map(activity => ({
        ID: activity.activityId,
        Name: activity.activityName,
        Status: activity.isActive ? 'Active' : 'Inactive',
        'All Devices': activity.allDevices,
        Shared: activity.shared,
        'Full Block': activity.isFullBlock,
        'Requires Scan': activity.requireScan
      }))

      const worksheet = XLSXUtils.json_to_sheet(dataToExport)
      const workbook = XLSXUtils.book_new()
      XLSXUtils.book_append_sheet(workbook, worksheet, 'Activities')

      // Ajustar el ancho de las columnas
      const maxWidth = dataToExport.reduce((w, r) => Math.max(w, r.Name.length), 10)
      worksheet['!cols'] = [
        { wch: maxWidth }, // Name
        { wch: 15 }, // Status
        { wch: 15 }, // All Devices
        { wch: 15 }, // Shared
        { wch: 15 } // Full Block
      ]

      const excelBuffer = XLSXWrite(workbook, {
        bookType: 'xlsx',
        type: 'array'
      })

      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `activities_${new Date().toISOString().split('T')[0]}.xlsx`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      window.URL.revokeObjectURL(url)

      showAlert({
        text: t('mgmt.common.excelDownloaded'),
        icon: 'success'
      })
    } catch (error) {
      showAlert({
        text: t('mgmt.common.excelFailed'),
        icon: 'error'
      })
    }
  }

  const handleExportPDF = () => {
    try {
      const doc = new JsPDF()

      doc.setFontSize(16)
      doc.text(t('mgmt.activities.reportTitle'), 14, 15)
      doc.setFontSize(10)
      doc.text(t('mgmt.common.generatedOn', { date: new Date().toLocaleDateString() }), 14, 25)

      const tableColumns = [
        'Name',
        'Status',
        'All Devices',
        'Shared',
        'Full Block',
        'Requires Scan'
      ]

      const tableData = state.activities.map(activity => [
        activity?.activityName || activity?.activity_name || activity?.name || 'N/A',
        (activity?.isActive || activity?.is_active) ? 'Active' : 'Inactive',
        (activity?.allDevices || activity?.all_devices) ? 'Yes' : 'No',
        activity?.shared ? 'Yes' : 'No',
        (activity?.isFullBlock || activity?.is_full_block) ? 'Yes' : 'No',
        ((activity?.requireScan ?? activity?.require_scan) ?? true) ? 'Yes' : 'No'
      ])

      doc.autoTable({
        startY: 30,
        head: [tableColumns],
        body: tableData,
        theme: 'grid',
        styles: {
          fontSize: 8,
          cellPadding: 2,
          overflow: 'linebreak'
        },
        headStyles: {
          fillColor: [67, 160, 71],
          textColor: 255,
          fontSize: 9,
          fontStyle: 'bold'
        },
        columnStyles: {
          0: { cellWidth: 'auto' }, // Name
          1: { cellWidth: 30 }, // Status
          2: { cellWidth: 30 }, // All Devices
          3: { cellWidth: 30 }, // Shared
          4: { cellWidth: 30 } // Full Block
        }
      })

      doc.save(`activities_${new Date().toISOString().split('T')[0]}.pdf`)

      showAlert({
        text: t('mgmt.common.pdfDownloaded'),
        icon: 'success'
      })
    } catch (error) {
      console.error('Error exporting to PDF:', error)
      showAlert({
        text: t('mgmt.common.pdfFailed'),
        icon: 'error'
      })
    }
  }

  const handleEdit = (activity) => {
    setState((s) => ({ ...s, selectedActivity: activity, isEditActivityModalOpen: true }))
  }

  const handleDelete = (activity) => {
    setState(s => ({ ...s, showDeleteConfirm: true, pendingDelete: activity }))
  }

  const confirmDelete = async () => {
    const activity = state.pendingDelete
    setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))
    try {
      await makeRequest({
        method: 'delete',
        url: `/management/activities/${activity.activityId}`,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` },
        alertResult: true
      })

      fetchActivities()
    } catch (error) {
      const serverMessage = error?.response?.data?.message || error.message

      const isConstraintError =
        serverMessage.includes('violates foreign key constraint') ||
        serverMessage.includes('cannot be deleted')

      showAlert({
        text: isConstraintError
          ? t('mgmt.activities.constraintError')
          : serverMessage,
        icon: isConstraintError ? 'warning' : 'error'
      })
    }
  }

  const handleSave = async (formData) => {
    const htmlMessage = getFormattedMissingFieldsMessage(formData, ['activityName'])
    if (htmlMessage) {
      showAlert({ html: htmlMessage, icon: 'warning' })
      return
    }

    try {
      const config = {
        method: 'post',
        url: `management/activities/${user.farmId}`,
        data: formData,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` },
        alertResult: true
      }
      if (state.selectedActivity && state.selectedActivity.activityId) {
        config.method = 'put'
        config.url = `/management/activities/${state.selectedActivity.activityId}`
      }
      await makeRequest(config)
    } catch (error) {
      console.error('Error saving activity:', error)
      showAlert({ text: error.message, icon: 'error' })
    }
    setState((s) => ({ ...s, isAddActivityModalOpen: false, isEditActivityModalOpen: false }))
  }

  const handleAddActivity = () => {
    setState((s) => ({
      ...s,
      isAddActivityModalOpen: true,
      selectedActivity: null
    }))
  }

  const filterActivities = state.activities.filter(data =>
    data.activityName.toLowerCase().includes(state.searchTerm)
  )

  const activitiesTableData = {
    columns: [
      {
        key: 'activityName',
        labelKey: 'mgmt.activities.cols.name',
        cell: (value, row) => <span>{value ?? row?.activity_name ?? 'N/A'}</span>
      },
      {
        key: 'isActive',
        labelKey: 'mgmt.activities.cols.status',
        cell: (value, row) => (
          <span className={`activities-status ${value}`}>
            {value ? t('mgmt.activities.active') : t('mgmt.activities.inactive')}
          </span>
        )
      },
      {
        key: 'allDevices',
        labelKey: 'mgmt.activities.cols.deviceAccess',
        cell: (value) => (value ? t('mgmt.common.yes') : t('mgmt.common.no'))
      },
      {
        key: 'shared',
        labelKey: 'mgmt.activities.cols.sharedTeam',
        cell: (value) => (value ? t('mgmt.common.yes') : t('mgmt.common.no'))
      },
      {
        key: 'isFullBlock',
        labelKey: 'mgmt.activities.cols.fullBlock',
        cell: (value, row) => ((value ?? row?.is_full_block) ? t('mgmt.common.yes') : t('mgmt.common.no'))
      },
      {
        key: 'requireScan',
        labelKey: 'mgmt.activities.cols.requiresScan',
        cell: (value, row) => (((value ?? row?.require_scan) ?? true) ? t('mgmt.common.yes') : t('mgmt.common.no'))
      },
      {
        key: 'actions',
        labelKey: 'mgmt.activities.cols.manage',
        cell: (value, activity) => (
          <div className='flex items-center gap-2'>
            <button onClick={() => handleEdit(activity)} title={t('common.edit')} className='p-1 rounded transition'>
              <PencilSquareIcon className='w-5 h-5 text-blue-500' />
            </button>
            <button onClick={() => handleDelete(activity)} title={t('common.delete')} className='p-1 rounded transition'>
              <TrashIcon className='w-5 h-5 text-red-500' />
            </button>
          </div>
        )
      }
    ],
    rows: Array.isArray(filterActivities)
      ? filterActivities.filter(row => row && typeof row === 'object')
      : []
  }

  if (state.loading) {
    return <PageLoadingState message={t('mgmt.activities.loading')} rows={8} />
  }

  return (
    <>
      <ActionCardComponent>
        <input
          type='text'
          placeholder={t('mgmt.activities.searchPlaceholder')}
          value={state.searchTerm}
          onChange={(e) => setState(s => ({ ...s, searchTerm: e.target.value.toLowerCase() }))}
          className='search-input'
          data-align='left'
        />
        <ButtonComponent onClick={handleAddActivity}>
          <PlusIcon className='w-5 h-5' />
          <span className='font-medium'>{t('mgmt.activities.new')}</span>
        </ButtonComponent>

        <ButtonComponent onClick={handleExportExcel}>
          <DocumentArrowDownIcon className='w-5 h-5' />
          <span className='font-medium'>{t('common.exportExcel')}</span>
        </ButtonComponent>

        <ButtonComponent onClick={handleExportPDF}>
          <DocumentArrowDownIcon className='w-5 h-5' />
          <span className='font-medium'>{t('mgmt.common.exportPdf')}</span>
        </ButtonComponent>
      </ActionCardComponent>

      <TableComponent tableData={activitiesTableData} defaultSortColumn={activitiesTableData.columns[0]?.key} />

      {/* <div className='pagination'>
          <span className='pagination-info'>
            showing {startIndex + 1}-{Math.min(endIndex, filteredActivities.length)} de {filteredActivities.length}
          </span>
          <div className='pagination-controls'>
            <button
              className='pagination-button'
              onClick={handlePageChange}
              disabled={page === 1}
            >
              <ChevronLeftIcon className='w-5 h-5' />
            </button>
            {[...Array(totalPages)].map((_, index) => {
              const pageNumber = index + 1
              if (
                pageNumber === 1 ||
                pageNumber === totalPages ||
                (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
              ) {
                return (
                  <button
                    key={pageNumber}
                    className={`pagination-button ${pageNumber === currentPage ? 'active' : ''}`}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                )
              }
              if (
                pageNumber === currentPage - 2 ||
                pageNumber === currentPage + 2
              ) {
                return <span key={pageNumber}>...</span>
              }
              return null
            })}
            <button
              className='pagination-button'
              onClick={handlePageChange}
              disabled={currentPage === totalPages}
            >
              <ChevronRightIcon className='w-5 h-5' />
            </button>
          </div>
        </div> */}

      {/* migrate to ModalComponent */}
      {state.isEditActivityModalOpen && (
        <EditActivityModal
          activity={state.selectedActivity}
          onClose={() => setState((s) => ({ ...s, isEditActivityModalOpen: false }))}
          onSave={handleSave}
        />
      )}

      {/* migrate to ModalComponent */}
      {state.isAddActivityModalOpen && (
        <AddActivityModal
          onClose={() => setState((s) => ({ ...s, isAddActivityModalOpen: false }))}
          onSave={handleSave}
        />
      )}

      <ModalComponent
        isOpen={state.showDeleteConfirm}
        onClose={() => setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))}
        title={t('mgmt.activities.deleteTitle')}
        maxWidth='sm'
        hideDefaultFooter
      >
        <div className='flex flex-col items-center gap-4 py-2'>
          <ExclamationTriangleIcon className='w-12 h-12 text-red-400' />
          <p className='text-sm text-gray-700 text-center'>
            {t('mgmt.activities.removeConfirm', { name: state.pendingDelete?.activityName ?? '' })}
            <br />
            <span className='text-gray-500'>{t('mgmt.common.confirmDelete')}</span>
          </p>
          <div className='flex justify-end gap-3 w-full mt-2'>
            <button
              type='button'
              onClick={() => setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))}
              className='px-4 py-2 rounded bg-gray-100 text-greenPrimary hover:bg-gray-200 transition'
            >
              {t('mgmt.common.cancel')}
            </button>
            <button
              type='button'
              onClick={confirmDelete}
              className='px-4 py-2 rounded bg-red-500 text-white hover:bg-red-600 transition'
            >
              {t('mgmt.common.delete')}
            </button>
          </div>
        </div>
      </ModalComponent>
    </>
  )
}

export default ActivitiesManagement
